/**
 * DeepSeek AI API Integration
 * Generates Amazon product reviews using AI
 */

const DEEPSEEK_API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY || 'sk-f0a56900628c49d3b6ea5d6e0b3378c8';
const DEEPSEEK_ENDPOINT = 'https://api.deepseek.com/v1/chat/completions';
const MODEL = 'deepseek-chat';

/**
 * Generate a review using DeepSeek AI
 * @param {number} stars - Number of stars (1-5)
 * @param {string} tone - Review tone (Enthusiastic/Romantic/Fun/Detailed)
 * @returns {Promise<string>} - Generated review text
 */
export async function generateReview(stars, tone) {
  // Build a natural, authentic prompt based on tone
  const toneInstructions = {
    'Enthusiastic': 'excited and positive',
    'Romantic': 'sweet and loving',
    'Fun': 'playful and lighthearted',
    'Detailed': 'thoughtful and specific'
  };

  const toneStyle = toneInstructions[tone] || 'genuine and honest';

  const systemPrompt = `You are writing a quick, authentic Amazon review as a real person who bought a couples tower game for their partner. Write like you're texting a friend - casual, natural, and from the heart. Keep it SHORT (250-400 characters max). No marketing language, just genuine thoughts.`;

  const userPrompt = `Write a ${stars}-star review for a couples tower game. Be ${toneStyle}. Write VERY SHORT - just 2-4 sentences max (250-400 characters total). Sound like a real person typing on their phone, not a professional reviewer. ${stars <= 3 ? 'Mention what could be better.' : 'Share what you genuinely liked.'} Don't use exclamation marks excessively. Be natural and casual.`;

  try {
    const response = await fetch(DEEPSEEK_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: JSON.stringify({
        model: MODEL,
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        temperature: 0.8,
        max_tokens: 150
      })
    });

    if (!response.ok) {
      throw new Error(`DeepSeek API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (data.choices && data.choices.length > 0) {
      return data.choices[0].message.content.trim();
    } else {
      throw new Error('No review generated');
    }
  } catch (error) {
    console.error('Error generating review:', error);
    throw new Error('Failed to generate review. Please try again.');
  }
}
